<template>
    <div class="wiki-home">
        <div class="wiki-header">
            <div class="header-left">
                <h1 class="title">Wiki</h1>
                <div class="wiki-tabs">
                    <span
                        :class="['tab-item', { 'is-active': isDocsTab }]"
                        @click="switchTab('docs')"
                    >Documents</span>
                    <span
                        :class="['tab-item', { 'is-active': isWordTab }]"
                        @click="switchTab('word')"
                    >Word Lookup</span>
                </div>
            </div>
            <div class="header-right" v-if="isDocsTab">
                <el-input
                    v-model="searchQuery"
                    placeholder="Search documents..."
                    prefix-icon="Search"
                    style="width: 240px; margin-right: 12px;"
                    clearable
                />
                <el-select
                    v-model="selectedTag"
                    placeholder="Filter by tag"
                    clearable
                    style="width: 180px; margin-right: 12px;"
                >
                    <el-option
                        v-for="tag in allTags"
                        :key="tag"
                        :label="tag"
                        :value="tag"
                    />
                </el-select>
                <el-button type="primary" @click="goToNew">
                    New Document
                </el-button>
            </div>
        </div>

        <router-view v-if="isSubPage" />

        <div v-if="!isSubPage && isDocsTab && loading" class="loading">
            <el-icon class="is-loading"><Loading /></el-icon>
            <span>Loading...</span>
        </div>

        <div v-else-if="!isSubPage && isDocsTab && filteredDocs.length === 0" class="empty">
            <el-empty description="No documents found">
                <el-button @click="goToNew">Create First Document</el-button>
            </el-empty>
        </div>

        <template v-else-if="!isSubPage && isDocsTab">
            <div class="doc-table-wrapper">
                <table class="doc-table">
                    <thead>
                        <tr>
                            <th class="col-title">Title</th>
                            <th class="col-tags">Tags</th>
                            <th class="col-type">Type</th>
                            <th class="col-date">Updated</th>
                            <th class="col-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="doc in paginatedDocs" :key="doc.id" @click="goToView(doc.id)">
                            <td class="col-title">
                                <span class="doc-title-link">{{ doc.title }}</span>
                            </td>
                            <td class="col-tags">
                                <div class="doc-tags">
                                    <el-tag
                                        v-for="tag in doc.tags"
                                        :key="tag"
                                        size="small"
                                        type="info"
                                    >
                                        {{ tag }}
                                    </el-tag>
                                </div>
                            </td>
                            <td class="col-type">
                                <span class="doc-type-badge">{{ doc.type }}</span>
                            </td>
                            <td class="col-date">{{ formatDate(doc.updatedAt) }}</td>
                            <td class="col-actions" @click.stop>
                                <el-button size="small" link @click="goToEdit(doc.id)">Edit</el-button>
                                <el-button size="small" link type="danger" @click="handleDelete(doc)">Delete</el-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pagination-wrapper" v-if="totalPages > 1">
                <el-pagination
                    v-model:current-page="currentPage"
                    :page-size="pageSize"
                    :total="filteredDocs.length"
                    layout="prev, pager, next"
                    background
                    small
                />
            </div>
        </template>

        <el-dialog
            v-model="deleteDialogVisible"
            title="Delete Document"
            width="400px"
        >
            <p>Are you sure you want to delete "{{ docToDelete?.title }}"?</p>
            <template #footer>
                <el-button @click="deleteDialogVisible = false">Cancel</el-button>
                <el-button @click="confirmDelete" :loading="deleting" class="delete-btn">
                    Delete
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Loading } from '@element-plus/icons-vue';
import { wikiApi } from '../../api/wiki';

export default {
    components: {
        Loading
    },
    setup() {
        const router = useRouter();
        const route = useRoute();
        const docs = ref([]);
        const loading = ref(false);
        const searchQuery = ref('');
        const selectedTag = ref('');
        const deleteDialogVisible = ref(false);
        const docToDelete = ref(null);
        const deleting = ref(false);
        const currentPage = ref(1);
        const pageSize = 20;

        const isDocsTab = computed(() => !route.path.startsWith('/wiki/word'));
        const isWordTab = computed(() => route.path.startsWith('/wiki/word'));
        const isSubPage = computed(() => route.path !== '/wiki');

        const switchTab = (tab) => {
            if (tab === 'word') {
                router.push('/wiki/word');
            } else {
                router.push('/wiki');
            }
        };

        const allTags = computed(() => {
            const tags = new Set();
            docs.value.forEach(doc => {
                if (doc.tags) {
                    doc.tags.forEach(tag => tags.add(tag));
                }
            });
            return Array.from(tags).sort();
        });

        const filteredDocs = computed(() => {
            let result = docs.value;

            if (searchQuery.value) {
                const query = searchQuery.value.toLowerCase();
                result = result.filter(doc =>
                    doc.title?.toLowerCase().includes(query) ||
                    doc.tags?.some(tag => tag?.toLowerCase().includes(query))
                );
            }

            if (selectedTag.value) {
                result = result.filter(doc =>
                    doc.tags?.includes(selectedTag.value)
                );
            }

            return result.sort((a, b) => b.updatedAt - a.updatedAt);
        });

        const totalPages = computed(() => Math.ceil(filteredDocs.value.length / pageSize));

        const paginatedDocs = computed(() => {
            const start = (currentPage.value - 1) * pageSize;
            return filteredDocs.value.slice(start, start + pageSize);
        });

        const fetchDocs = async () => {
            loading.value = true;
            try {
                const response = await wikiApi.listDocs();
                docs.value = response.data || [];
            } catch (e) {
                console.error('Failed to fetch docs:', e);
                ElMessage.error('Failed to load documents');
            } finally {
                loading.value = false;
            }
        };

        const goToNew = () => {
            router.push('/wiki/new');
        };

        const goToView = (id) => {
            router.push(`/wiki/${id}`);
        };

        const goToEdit = (id) => {
            router.push(`/wiki/${id}/edit`);
        };

        const handleDelete = (doc) => {
            docToDelete.value = doc;
            deleteDialogVisible.value = true;
        };

        const confirmDelete = async () => {
            if (!docToDelete.value) return;

            deleting.value = true;
            try {
                await wikiApi.deleteDoc(docToDelete.value.id);
                ElMessage.success('Document deleted');
                await fetchDocs();
                deleteDialogVisible.value = false;
            } catch (e) {
                console.error('Failed to delete doc:', e);
                ElMessage.error('Failed to delete document');
            } finally {
                deleting.value = false;
                docToDelete.value = null;
            }
        };

        const formatDate = (timestamp) => {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        onMounted(() => {
            fetchDocs();
        });

        return {
            docs,
            loading,
            searchQuery,
            selectedTag,
            allTags,
            filteredDocs,
            paginatedDocs,
            currentPage,
            pageSize,
            totalPages,
            deleteDialogVisible,
            docToDelete,
            deleting,
            isDocsTab,
            isWordTab,
            isSubPage,
            switchTab,
            goToNew,
            goToView,
            goToEdit,
            handleDelete,
            confirmDelete,
            formatDate,
            route
        };
    }
};
</script>

<style scoped>
.wiki-home {
    padding: var(--spacing-lg);
    max-width: 1200px;
    margin: 0 auto;
}

.wiki-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-lg);
    padding-bottom: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
}

.header-left {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
}

.wiki-tabs {
    display: flex;
    gap: 4px;
    background: var(--bg-secondary);
    padding: 3px;
    border-radius: 8px;
}

.tab-item {
    padding: 6px 16px;
    border-radius: 6px;
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    user-select: none;
}

.tab-item:hover {
    color: var(--text-primary);
}

.tab-item.is-active {
    background: var(--bg-card);
    color: var(--color-primary);
    box-shadow: var(--shadow-sm);
}

.title {
    margin: 0;
    color: var(--text-primary);
    font-size: 28px;
    font-weight: 700;
    font-family: var(--font-heading);
}

.count {
    font-size: 13px;
    color: var(--text-secondary);
    background: var(--bg-secondary);
    padding: 3px 10px;
    border-radius: 12px;
    font-weight: 500;
}

.header-right {
    display: flex;
    align-items: center;
}

.loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-md);
    padding: 80px 0;
    color: var(--text-secondary);
    font-size: 14px;
}

.loading .el-icon {
    font-size: 24px;
}

.empty {
    padding: 80px 0;
}

.doc-table-wrapper {
    background: var(--bg-card);
    border-radius: 8px;
    border: 1px solid var(--border-light);
    overflow: hidden;
}

.doc-table {
    width: 100%;
    border-collapse: collapse;
}

.doc-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-light);
}

.doc-table td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-light);
    vertical-align: middle;
}

.doc-table tbody tr {
    cursor: pointer;
    transition: background 0.15s;
}

.doc-table tbody tr:hover {
    background: var(--bg-secondary);
}

.doc-table tbody tr:last-child td {
    border-bottom: none;
}

.col-title {
    min-width: 200px;
}

.col-tags {
    min-width: 120px;
}

.col-type {
    width: 80px;
}

.col-date {
    width: 160px;
    white-space: nowrap;
}

.col-actions {
    width: 120px;
    white-space: nowrap;
}

.doc-title-link {
    color: var(--text-primary);
    font-weight: 500;
    font-size: 14px;
}

.doc-table tbody tr:hover .doc-title-link {
    color: #409eff;
}

.doc-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.doc-type-badge {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    background: var(--bg-secondary);
    padding: 2px 8px;
    border-radius: 4px;
    color: var(--text-secondary);
}

.pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: var(--spacing-lg);
}

.delete-btn {
    color: #f56c6c;
    border-color: #f56c6c;
}

.delete-btn:hover {
    background-color: var(--bg-hover, #fef0f0);
}
</style>
